import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const checklist = [
  "Ideation & Use Case Identification",
  "Data Exploration and Collection",
  "Model Selection",
  "Creative Outputs",
  "Integration and Deployment",
  "User Experience Enhancement",
  "Ethical Considerations",
  "Continuous Innovation",
  "Support and Maintenance",
];

const Counter = ({ target }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref} data-testid="ai-projects-counter" className="font-heading font-bold text-4xl text-white">
      {value}+
    </span>
  );
};

const AISection = () => (
  <section data-testid="ai-section" className="py-24 lg:py-16 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
          Boosting Efficiency and Productivity with AI
        </span>
        <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
          Unleashing AI-Powered Creativity and Intelligence for Business Growth
        </h2>
        <p className="mt-5 text-base md:text-lg text-slate-500 leading-relaxed">
          We empower businesses to harness the limitless possibilities of AI-driven creativity and intelligence,
          unlocking unprecedented value and shaping the future of industries to thrive in the digital age.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
          {checklist.map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="flex items-start gap-3"
              data-testid={`ai-checklist-item-${i + 1}`}
            >
              <span className="mt-0.5 w-5 h-5 rounded-full bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                <Check size={12} strokeWidth={3} />
              </span>
              <span className="text-sm font-medium text-slate-700">{item}</span>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href="/services/technology-services"
            data-testid="ai-discover-more-button"
            className="group inline-flex items-center gap-2.5 rounded-lg bg-miti-blue px-6 py-3 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
          >
            Discover More
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="/services/technology-services"
            data-testid="ai-see-all-services-button"
            className="inline-flex items-center gap-2.5 rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:border-miti-blue hover:text-miti-blue transition-colors"
          >
            See All Service
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
          <img
            src="/assets/ai-brain.png"
            alt="AI Services"
            className="w-full h-[420px] lg:h-[520px] object-cover"
          />
        </div>
        <div className="absolute -bottom-8 -left-4 sm:-left-8 rounded-2xl bg-miti-navy px-8 py-6 shadow-xl">
          <Counter target={150} />
          <p className="mt-1 text-xs font-semibold tracking-[0.2em] uppercase text-sky-300">Projects Completed</p>
        </div>
      </motion.div>
    </div>
  </section>
);

export default AISection;
